import {
    adjustSiteStock,
    markQcOnBehalf,
    STOCK_ADJUSTMENT_REASONS,
} from '@api/cm-sites';
import { markBatchQc, type BatchRegisterRow } from '@api/vendor-portal';
import SegmentedToggle from '@components/ui/SegmentedToggle';
import StatusPill from '@components/ui/StatusPill';
import useToaster from '@hooks/toaster/useToaster';
import {
    Button,
    Drawer,
    Group,
    NumberInput,
    SegmentedControl,
    Select,
    Stack,
    Text,
    Textarea,
} from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { readApiError } from '@utils/apiError';
import { useEffect, useState, type FC } from 'react';

import styles from './BatchPanel.module.css';
import { qcDisplayStatus } from './batchQcSplit';
import { resolveQcSplit } from './qcSplit';

interface BatchPanelProps {
    batch: BatchRegisterRow | null;
    onClose: () => void;
    onChanged: () => void;
    /**
     * The site being looked at, set only on the brand's view of it.
     *
     * DELIBERATELY NOT `onBehalfOf`. That carries "who at the site reported this", which every
     * other brand write needs and this one cannot have — a verdict is the brand's own, not
     * something the site told them. Gating on it meant the QC buttons stayed hidden until a
     * coordinator typed a name into a field that does not apply, which read as the feature
     * being broken.
     */
    brandSite?: { warehouseId: string } | null;
    /**
     * Whether this panel may correct the batch's quantity, and where to send it.
     *
     * A SEPARATE VERB FROM QC, on purpose: a verdict says the goods are good or bad, an
     * adjustment says the count was wrong. Conflating them would let "we found ten fewer" be
     * filed as a quality failure, which is the variance signal the brand watches for.
     *
     * Done INSIDE this panel rather than by opening a form over it. This drawer is already a
     * form — it has a quantity field and a reason field — so a second one on top put two of
     * each on screen at once, and left the person deciding which "why" was which.
     */
    canAdjust?: boolean;
    onAdjusted?: () => void;
    /** Opens the batch's history in place of this panel; the host swaps the drawers. */
    onHistory?: () => void;
}

/**
 * The same two questions asked of the movements that answer them for each kind.
 *
 * A finished batch is MADE and DISPATCHED; a material batch is RECEIVED and USED. The register
 * relabels the identical columns this way, and the panel has to agree with the row that opened
 * it or the two read as different numbers.
 */
const FIGURE_LABELS: Record<string, { inbound: string; outbound: string }> = {
    product: { inbound: 'Made', outbound: 'Dispatched' },
    material: { inbound: 'Received', outbound: 'Used' },
};

/**
 * One batch, and the verdict on it.
 *
 * OPENED FROM THE REGISTER, which is where somebody working a QC list already is. Before this
 * the register could show that eight batches were waiting and offer nothing to do about it —
 * the verdict lived on the item tabs, so clearing a list meant leaving it, finding the item,
 * opening its panel and picking the batch out again.
 *
 * BATCH-GRAIN ON PURPOSE. The older panel takes an ITEM and lists its batches, which is right
 * when the question is "what have I got of this"; here the question is "what about this one",
 * and showing its siblings would be answering something nobody asked.
 *
 * A REASON IS TAKEN, NEVER DEMANDED. The server requires one only where it matters — releasing
 * something that failed, or failing something already in use — and asking for one on an
 * ordinary pass is how a form teaches people to type "ok".
 */
const BatchPanel: FC<BatchPanelProps> = ({
    batch,
    onClose,
    onChanged,
    brandSite,
    canAdjust = false,
    onAdjusted,
    onHistory,
}) => {
    const { showToastSuccess, showToastError } = useToaster();

    const [notes, setNotes] = useState('');
    const [pending, setPending] = useState<string | null>(null);
    /**
     * How much of the batch failed. Held as the rejected count rather than the accepted one
     * because that is the number somebody has in front of them — they counted the rejects, and
     * what passed is the remainder.
     */
    const [rejected, setRejected] = useState<number | ''>(0);

    /**
     * Two tabs, because these are two unrelated jobs about the same batch.
     *
     * A verdict says the goods are good or bad; an adjustment says the count was wrong. Sharing
     * one surface meant a greyed-out Fail QC sat beside a quantity field, and nobody could tell
     * which activity the panel was for. Separated, each tab shows only its own fields and only
     * its own buttons, and neither has to explain the other.
     */
    const [tab, setTab] = useState<'qc' | 'adjust'>('qc');
    const [direction, setDirection] = useState<'more' | 'less'>('more');
    const [amount, setAmount] = useState<number | ''>('');
    const [reasonCode, setReasonCode] = useState<string | null>(null);
    /**
     * Whether the drawer is showing the send-back-to-QC step.
     *
     * An in-place body swap rather than a modal over the drawer: a stacked dialog loses the
     * z-index fight, and this drawer is already a form — the hold needs only its own copy,
     * the same Why box, and its own pair of buttons.
     */
    const [holding, setHolding] = useState(false);

    useEffect(() => {
        setNotes('');
        // Seeded with the total already on the record, because the box edits the TOTAL: the
        // batch states its rejected count absolutely and the system moves the difference.
        setRejected(Number(batch?.qcFailedQuantity ?? 0) || 0);
        setTab('qc');
        setDirection('more');
        setAmount('');
        setReasonCode(null);
        setHolding(false);
    }, [batch]);

    if (!batch) return null;

    const isReleased = batch.qcStatus === 'PASSED';
    const labels =
        FIGURE_LABELS[batch.itemType ?? 'product'] ?? FIGURE_LABELS.product;

    /**
     * Whether this viewer may rule on this batch.
     *
     * The site checks material at its own gate — it has to, because consumption draws only on
     * released stock. Finished goods are the brand's call, so on the site's own screen a
     * product batch opens read-only. The server refuses it either way; this is so nobody is
     * offered a button that will fail.
     */
    const isFinishedGoods = batch.itemType === 'product';
    const canRule = Boolean(brandSite) || !isFinishedGoods;
    const needsReason = !notes.trim();

    /**
     * Splitting a verdict is the brand's, on finished goods, and only there.
     *
     * The site's own material gate stays a single call on the whole drum — it releases stock
     * for its own production runs, and a partial release there would need the FEFO picker to
     * reason about slices of a batch it cannot address separately.
     */
    const canSplit = Boolean(brandSite) && isFinishedGoods;

    /** Correcting is the brand's own act, like a verdict, so it needs the site and nothing else. */
    const submitAdjustment = async () => {
        if (!brandSite || !reasonCode || amount === '' || amount <= 0) return;

        setPending('ADJUST');
        try {
            await adjustSiteStock({
                warehouseId: brandSite.warehouseId,
                itemId: batch.itemId ?? '',
                itemType: isFinishedGoods ? 'product' : 'material',
                quantity: Number(amount),
                incoming: direction === 'more',
                reasonCode,
                // Always pinned: this panel is open on one batch, so there is nothing to pick.
                batchId: batch.batchId,
                notes: notes.trim() || undefined,
            });

            showToastSuccess({
                title: 'Corrected',
                subtitle: `${direction === 'more' ? 'Added' : 'Removed'} ${amount} on ${batch.code}. The change is on the ledger with its reason.`,
            });
            onAdjusted?.();
            onChanged();
            onClose();
        } catch (error) {
            showToastError({
                title: 'Not recorded',
                subtitle: readApiError(
                    error,
                    'The correction could not be saved.',
                ),
            });
        } finally {
            setPending(null);
        }
    };
    /** What the record already says failed — the seed the box edits against. */
    const previousRejected = Number(batch.qcFailedQuantity ?? 0) || 0;
    const split = resolveQcSplit({
        made: batch.made,
        remaining: batch.remaining,
        previousRejected,
        rejected,
    });
    // A rejection strands stock and the site has to know what to fix, exactly as an outright
    // failure does. The server refuses it too; this says so before the round trip.
    const blockedPass =
        canSplit &&
        (split.isPartial || split.hasChange) &&
        (Boolean(split.error) || needsReason);

    const submit = async (qcStatus: 'PASSED' | 'FAILED') => {
        setPending(qcStatus);
        // Only a pass carries a split: failing blocks the whole batch, and what physically
        // leaves after that is recorded as a return or a disposal. A correction is a re-pass
        // with new totals, so a changed-but-zero total still travels as a split.
        const partial =
            canSplit &&
            (split.isPartial || split.hasChange) &&
            qcStatus === 'PASSED';
        try {
            const { notable } = brandSite
                ? await markQcOnBehalf(brandSite.warehouseId, batch.batchId, {
                      qcStatus,
                      notes: notes.trim() || undefined,
                      ...(partial
                          ? {
                                passedQuantity: split.accepted,
                                failedQuantity: split.total,
                            }
                          : {}),
                  })
                : await markBatchQc(batch.batchId, {
                      qcStatus,
                      notes: notes.trim() || undefined,
                  });
            showToastSuccess({
                title: partial
                    ? `${batch.code} — ${split.accepted} passed, ${split.total} rejected`
                    : `${batch.code} ${qcStatus === 'PASSED' ? 'released' : 'withdrawn'}`,
                subtitle: partial
                    ? split.delta > 0
                        ? `The ${split.delta} newly rejected have been written off. ${split.accepted} stand passed.`
                        : split.delta < 0
                          ? `${-split.delta} previously rejected returned to the shelf. ${split.accepted} stand passed.`
                          : `The ${split.total} rejected were already written off. ${split.accepted} stand passed.`
                    : qcStatus === 'PASSED'
                      ? 'It can be used and shipped.'
                      : 'Nothing can be run or shipped from it until it is released.',
                ...(notable
                    ? {
                          subtitle:
                              'The brand is shown this and the reason for it.',
                      }
                    : {}),
            });
            onChanged();
            onClose();
        } catch (err: any) {
            showToastError({
                title: 'Could not record the verdict',
                subtitle:
                    err?.error?.message ??
                    err?.message ??
                    'Something went wrong.',
            });
        } finally {
            setPending(null);
        }
    };

    /**
     * The hold: a judged batch back to awaiting-QC. A third decision beside the two verdicts,
     * never an undo — it moves no stock, keeps every write-off, and reopens the question. The
     * server requires the reason and surfaces the flip to the other side as notable.
     */
    const submitHold = async () => {
        setPending('PENDING');
        try {
            if (brandSite) {
                await markQcOnBehalf(brandSite.warehouseId, batch.batchId, {
                    qcStatus: 'PENDING',
                    notes: notes.trim() || undefined,
                });
            } else {
                await markBatchQc(batch.batchId, {
                    qcStatus: 'PENDING',
                    notes: notes.trim() || undefined,
                });
            }
            showToastSuccess({
                title: `${batch.code} sent back to QC`,
                subtitle:
                    'Back on the QC Pending list. Nothing can use or ship it until a new verdict lands.',
            });
            onChanged();
            onClose();
        } catch (error) {
            showToastError({
                title: 'Could not record the hold',
                subtitle: readApiError(error, 'The hold could not be saved.'),
            });
        } finally {
            setPending(null);
        }
    };

    /** The pill says what happened, not just what is stored: a pass with rejects is partial. */
    const displayStatus = qcDisplayStatus(batch);

    return (
        <Drawer
            opened={!!batch}
            onClose={onClose}
            position="right"
            size="md"
            title={
                <Group gap={8}>
                    <Text fz={16} fw={600} c={neutralColors.textPrimary}>
                        {batch.code}
                    </Text>
                    {onHistory && (
                        <Button size="xs" variant={ButtonVariant.SUBTLE} onClick={onHistory}>
                            History
                        </Button>
                    )}
                    {displayStatus && (
                        <StatusPill
                            label={displayStatus.replace('_', ' ')}
                            tone={
                                displayStatus === 'PASSED'
                                    ? 'positive'
                                    : displayStatus === 'FAILED' ||
                                        displayStatus === 'REJECTED'
                                      ? 'critical'
                                      : 'warning'
                            }
                        />
                    )}
                </Group>
            }
        >
            <Stack gap={16}>
                <Text fz={13} c={neutralColors.textSecondary}>
                    {batch.itemName ?? batch.itemId}
                </Text>

                {/*
                 * WHY "AT SITE" IS ITS OWN FIGURE, AND WHY DISPATCHED SITS BESIDE IT. The gap
                 * between what was made and what is here is the part people misread — 120 made
                 * and 100 present invites "where did 20 go", and the honest answer is that they
                 * already shipped. Showing the outbound figure makes the arithmetic visible
                 * instead of leaving it to be worked out.
                 *
                 * It is also the number that BOUNDS the decision below: a rejection is written
                 * off this balance, so the most that can be rejected is what is still standing
                 * here, not what the run originally produced.
                 */}
                <div className={styles.figures}>
                    <div className={styles.figure}>
                        <span className={styles.figureLabel}>
                            {labels.inbound}
                        </span>
                        <span className={styles.figureValue}>{batch.made}</span>
                    </div>
                    <div className={styles.figure}>
                        <span className={styles.figureLabel}>
                            {labels.outbound}
                        </span>
                        <span
                            className={`${styles.figureValue} ${
                                batch.dispatched ? '' : styles.figureMuted
                            }`}
                        >
                            {batch.dispatched}
                        </span>
                    </div>
                    <div className={styles.figure}>
                        <span className={styles.figureLabel}>At site</span>
                        <span
                            className={`${styles.figureValue} ${
                                batch.remaining ? '' : styles.figureMuted
                            }`}
                        >
                            {batch.remaining}
                        </span>
                    </div>
                </div>

                <Text fz={12} c={neutralColors.textMuted}>
                    Manufactured {batch.manufacturingDate ?? '—'} · expires{' '}
                    {batch.expiryDate ?? '—'}
                    {batch.qcMarkedAt ? ` · checked ${batch.qcMarkedAt}` : ''}
                </Text>

                {/* Only when there is a choice: one pill on its own is a control that does
                    nothing, and the site has no adjust tab to switch to. */}
                {canAdjust && (
                    <SegmentedToggle
                        value={tab}
                        onChange={(value) => setTab(value as 'qc' | 'adjust')}
                        options={[
                            { label: 'Quality check', value: 'qc' },
                            { label: 'Adjust quantity', value: 'adjust' },
                        ]}
                    />
                )}

                {/* What a previous verdict was arrived at from. Shown whenever it was recorded,
                    including to the site, which cannot set it but does need to know why 20 of
                    its batch went. */}
                {/* The hold step: the drawer body swaps in place. States what a hold does and
                    what it deliberately does not — write-offs are settled fact. */}
                {tab === 'qc' && holding && (
                    <Text fz={13} c={semanticColors.warning.dark}>
                        Back to awaiting QC: this batch returns to the QC
                        Pending list and its {batch.remaining} at site cannot be
                        used or shipped until someone rules on it again.
                        {previousRejected > 0
                            ? ` The ${previousRejected} already written off stay written off.`
                            : ''}{' '}
                        The {brandSite ? 'site' : 'brand'} is shown this hold
                        and your reason.
                    </Text>
                )}

                {tab === 'qc' &&
                    !holding &&
                    batch.qcFailedQuantity !== null &&
                    batch.qcPassedQuantity !== null && (
                        <Text fz={13} c={neutralColors.textSecondary}>
                            Checked as {batch.qcPassedQuantity} passed and{' '}
                            {batch.qcFailedQuantity} rejected. The rejected were
                            written off.
                        </Text>
                    )}

                {/* Only while something is actually standing: "withdrawing blocks the whole
                    0" is a sentence nobody should read. */}
                {tab === 'qc' &&
                    !holding &&
                    isReleased &&
                    batch.remaining > 0 && (
                        <Text fz={13} c={semanticColors.warning.dark}>
                            This batch is released and in use. Withdrawing it
                            blocks the whole {batch.remaining} at once
                            {canSplit
                                ? ', or change the rejected total below to withdraw part of it'
                                : ''}
                            .
                        </Text>
                    )}

                {tab === 'qc' && !canRule && (
                    <Text fz={13} c={neutralColors.textSecondary}>
                        Quality on finished goods is signed off by the brand.
                        They see this batch as awaiting QC and the verdict will
                        appear here once given.
                    </Text>
                )}

                {tab === 'adjust' && (
                    <Text fz={13} c={neutralColors.textSecondary}>
                        For a count that disagrees with the book. Nothing here
                        changes the quality verdict — it only corrects how much
                        is on the shelf.
                    </Text>
                )}

                {tab === 'adjust' && (
                    <>
                        <SegmentedControl
                            value={direction}
                            onChange={(value) =>
                                setDirection(value as 'more' | 'less')
                            }
                            data={[
                                { value: 'more', label: 'Count found more' },
                                { value: 'less', label: 'Count found less' },
                            ]}
                        />
                        {/* Bottom-aligned, not top-aligned: the two descriptions are
                            different lengths and either can wrap on a narrow drawer. Aligning
                            the tops lets one wrapped line push its box out of line with the
                            other. Aligning the bottoms keeps the two boxes level whatever the
                            copy above them does. */}
                        <Group grow align="flex-end">
                            <NumberInput
                                label="How many units"
                                description={
                                    <>
                                        <strong>The difference</strong>, not the
                                        new total
                                        {direction === 'less' &&
                                            ` (up to ${batch.remaining})`}
                                    </>
                                }
                                value={amount}
                                onChange={(value) =>
                                    setAmount(value === '' ? '' : Number(value))
                                }
                                min={0}
                                max={
                                    direction === 'less'
                                        ? batch.remaining
                                        : undefined
                                }
                                allowNegative={false}
                            />
                            <Select
                                label="Why"
                                description="Reason for change in count"
                                placeholder="Pick a reason"
                                data={[...STOCK_ADJUSTMENT_REASONS]}
                                value={reasonCode}
                                onChange={setReasonCode}
                            />
                        </Group>
                    </>
                )}

                {tab === 'qc' && !holding && canSplit && (
                    <>
                        <NumberInput
                            label={
                                previousRejected > 0
                                    ? 'Rejected — total for this batch'
                                    : 'Rejected'
                            }
                            description={
                                previousRejected > 0
                                    ? `Currently ${previousRejected} of the ${batch.made} made. Change the total and the difference is what moves.`
                                    : split.isPartial
                                      ? `${split.accepted} of ${batch.made} pass. The ${split.total} rejected come off the ${batch.remaining} at site.`
                                      : `Leave at zero if the whole batch passes. Up to ${split.maxRejectable} can be rejected — what is still at the site.`
                            }
                            value={rejected}
                            onChange={(value) =>
                                setRejected(value === '' ? '' : Number(value))
                            }
                            min={0}
                            max={split.maxRejectable}
                            error={split.error}
                            w={260}
                            allowNegative={false}
                        />
                        {/* One plain sentence stating the outcome being declared. The
                            movement mechanics stay in the toast; this is just the result. */}
                        {previousRejected > 0 &&
                            split.hasChange &&
                            !split.error && (
                                <Text
                                    fz={13}
                                    c={
                                        split.delta > 0
                                            ? semanticColors.warning.dark
                                            : semanticColors.positive.dark
                                    }
                                >
                                    {split.accepted} items have passed QC and{' '}
                                    {split.total} items are rejected.
                                </Text>
                            )}
                    </>
                )}

                {(tab === 'adjust' || canRule) && (
                    <Textarea
                        label={tab === 'adjust' ? 'Note' : 'Why'}
                        // One field, two jobs. The QC copy explains why a note is mandatory
                        // to fail; on the adjust tab the reason code above is what is
                        // required, and this only carries the detail the code cannot.
                        description={
                            tab === 'adjust'
                                ? 'Optional. Anything that explains the recount to whoever reads this later.'
                                : holding
                                  ? 'Required — a hold without a reason cannot be told apart from a mis-click.'
                                  : previousRejected > 0
                                    ? 'Required whenever the rejected total changes — the site sees it, and a number that moved without a reason starts an argument.'
                                    : 'Required to fail a batch — the site sees it, and “failed” on its own tells them nothing they can act on. Optional on a pass.'
                        }
                        placeholder={
                            tab === 'adjust'
                                ? 'Recounted the pallet with the site lead, two cartons were never scanned in…'
                                : 'Seams failed the pull test, lab report attached, retested clean…'
                        }
                        value={notes}
                        onChange={(e) => setNotes(e.currentTarget.value)}
                        minRows={3}
                        autosize
                    />
                )}

                <Group justify="flex-end" gap={12}>
                    {/* Each tab offers only its own verb. Nothing is greyed out to explain a
                        job this tab is not for. */}
                    {tab === 'adjust' ? (
                        <Button
                            variant={ButtonVariant.ACTION}
                            loading={pending === 'ADJUST'}
                            disabled={
                                amount === '' || amount <= 0 || !reasonCode
                            }
                            onClick={submitAdjustment}
                        >
                            Record adjustment
                        </Button>
                    ) : (
                        <>
                            {canRule && holding && (
                                <>
                                    <Button
                                        variant={ButtonVariant.SUBTLE}
                                        disabled={pending !== null}
                                        onClick={() => setHolding(false)}
                                    >
                                        Keep the verdict
                                    </Button>
                                    <Button
                                        variant={ButtonVariant.ACTION}
                                        loading={pending === 'PENDING'}
                                        disabled={needsReason}
                                        onClick={submitHold}
                                    >
                                        Send back to QC
                                    </Button>
                                </>
                            )}
                            {canRule && !holding && (
                                <>
                                    {/* The way back into quarantine, offered only when there is a verdict to question
                        AND stock still standing to reopen — a hold on a batch with nothing at
                        site would freeze nothing and mislead everyone. */}
                                    {(batch.qcStatus === 'PASSED' ||
                                        batch.qcStatus === 'FAILED') &&
                                        batch.remaining > 0 && (
                                            <Button
                                                variant={ButtonVariant.OUTLINED}
                                                me="auto"
                                                disabled={pending !== null}
                                                onClick={() => setHolding(true)}
                                            >
                                                Send back to QC
                                            </Button>
                                        )}
                                    {(batch.qcStatus !== 'PASSED' ||
                                        (canSplit && split.hasChange)) && (
                                        <Button
                                            variant={ButtonVariant.ACTION}
                                            loading={pending === 'PASSED'}
                                            disabled={blockedPass}
                                            onClick={() => submit('PASSED')}
                                        >
                                            {/* Not "Pass QC" once part of it has failed or the totals moved —
                                the action is no longer a clean pass, and calling it one would
                                misdescribe the movement it is about to make. */}
                                            {(split.isPartial ||
                                                split.hasChange) &&
                                            canSplit
                                                ? 'Record result'
                                                : 'Pass QC'}
                                        </Button>
                                    )}
                                    {/* Whole-batch fail steps aside while a split edit is live, so exactly one
                        action on screen matches the arithmetic being shown. */}
                                    {batch.qcStatus !== 'FAILED' &&
                                        !(canSplit && split.hasChange) && (
                                            <Button
                                                variant={
                                                    ButtonVariant.OUTLINED_DESTRUCTIVE
                                                }
                                                loading={pending === 'FAILED'}
                                                // A failure the site cannot act on is a failure that starts a phone
                                                // call. The server refuses it too; this just says so first.
                                                disabled={needsReason}
                                                onClick={() => submit('FAILED')}
                                            >
                                                Fail QC
                                            </Button>
                                        )}
                                </>
                            )}
                        </>
                    )}
                </Group>
            </Stack>
        </Drawer>
    );
};

export default BatchPanel;
